import React, { Dispatch, SetStateAction, useContext, useEffect } from 'react';
import {
  ISportUnityProps,
  ISportUnityUpdateParams,
} from '../../../../services/Sports/Unities/types';
import ModalComponent from '../../../../components/ModalComponent';
import { SportUnityFormSchema } from '../../../../services/Sports/Unities/schemas';
import zod from 'zod';
import { AppContext } from '../../../../context/AppContextProvider';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useCreateSportUnity,
  useUpdateSportUnity,
} from '../../../../services/Sports/Unities/hooks/useMutation';
import { Grid2 } from '@mui/material';
import TextFieldComponent from '../../../../components/TextFieldComponent';
import ButtonComponent from '../../../../components/ButtonComponent';
import SwitchComponent from '../../../../components/SwitchComponent';
import { useGetViaCepAdress } from '../../../../services/ViaCepService/hooks/useMutation';
import DropdownComponent from '../../../../components/DropdownComponent';
import { COUNTRYOPTIONS } from '../../../../services/AddressService/types';
import { t } from 'i18next';

interface ISportUnityEditModal {
  isOpen: boolean;
  onClose: () => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  handleSaveForm: () => void;
  selectedUnity?: ISportUnityProps;
}

export type SportUnityFormInput = zod.infer<typeof SportUnityFormSchema>;

const SportUnityEditModal = ({
  isOpen,
  onClose,
  handleSaveForm,
  selectedUnity,
}: ISportUnityEditModal) => {
  const methods = useForm<SportUnityFormInput>({
    resolver: zodResolver(SportUnityFormSchema),
    defaultValues: {
      name: '',
      address: {
        name: '',
        country: '',
        state: '',
        city: '',
        neighborhood: '',
        street: '',
        addrNumber: '',
        postalCode: '',
        complement: '',
      },
      hideVideosFromListing: false,
    },
  });
  const { control, reset, handleSubmit, setValue } = methods;
  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);
  const { mutateAsync: mutateCreateUnity } = useCreateSportUnity();
  const { mutateAsync: mutateUpdateUnity } = useUpdateSportUnity();
  const { mutateAsync: mutateGetViaCep } = useGetViaCepAdress();

  const getAddress = async (value?: string) => {
    if (!value) return;

    await mutateGetViaCep(value).then((address) => {
      setValue('address.postalCode', address.postalCode);
      setValue('address.city', address.city);
      setValue('address.state', address.state);
      setValue('address.street', address.street);
      setValue('address.neighborhood', address.neighborhood);
    });
  };

  const hancleClose = () => {
    reset();
    onClose();
  };

  const normalizeValues = (data: SportUnityFormInput) => {
    if (data.address.postalCode) {
      data.address.postalCode = data.address.postalCode.replace(/\D/g, '');
    }

    return data;
  };

  const handleEdition = async (data: any) => {
    setIsShowLoadingComponent(true);
    data = normalizeValues(data);
    if (selectedUnity) {
      await mutateUpdateUnity({
        id: selectedUnity.id,
        data: data,
      } as ISportUnityUpdateParams)
        .then(() => {
          resetAndShowMessage('Unidade Esportiva atualizada com sucesso!');
        })
        .finally(() => setIsShowLoadingComponent(false));
    } else {
      mutateCreateUnity(data)
        .then(() => {
          resetAndShowMessage('Unidade Esportiva criada com sucesso!');
        })
        .finally(() => setIsShowLoadingComponent(false));
    }
  };

  const resetAndShowMessage = (message: string) => {
    reset();
    onClose();
    showSnackbar(message, 'success');
    handleSaveForm();
  };

  useEffect(() => {
    if (selectedUnity && selectedUnity.id) {
      reset(selectedUnity, { keepDefaultValues: true });
    } else {
      reset();
    }
  }, [selectedUnity]);

  return (
    <ModalComponent
      title={
        selectedUnity
          ? t('sportsPage.unities.sportUnityModal.editTitle')
          : t('sportsPage.unities.sportUnityModal.addTitle')
      }
      onClose={hancleClose}
      isOpen={isOpen}
    >
      <Grid2
        container
        direction={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        size={12}
        spacing={3}
      >
        <Grid2
          display={'flex'}
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent={'center'}
          alignItems={{ xs: 'center', md: 'normal' }}
          size={12}
          gap={{ xs: 0, md: 3 }}
        >
          <Grid2
            size={{ xs: 12, lg: 6 }}
            display={'column'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('sportsPage.unities.sportUnityModal.name')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'name'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('sportsPage.unities.sportUnityModal.postalCode')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  onBlur={() => getAddress(value)}
                  errorMessage={error?.message}
                  value={value}
                  mask={'00000-000'}
                />
              )}
              name={'address.postalCode'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DropdownComponent
                  label={t('sportsPage.unities.sportUnityModal.country')}
                  options={COUNTRYOPTIONS}
                  optionLabel={'label'}
                  optionValue={'value'}
                  errorMessage={error?.message}
                  value={value}
                  onChange={onChange}
                />
              )}
              name={'address.country'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('sportsPage.unities.sportUnityModal.state')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'address.state'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('sportsPage.unities.sportUnityModal.city')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'address.city'}
              control={control}
            />
          </Grid2>
          <Grid2
            size={{ xs: 12, lg: 6 }}
            display={'column'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('sportsPage.unities.sportUnityModal.neighborhood')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'address.neighborhood'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('sportsPage.unities.sportUnityModal.street')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'address.street'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('sportsPage.unities.sportUnityModal.number')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  isOptional={true}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'address.addrNumber'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('sportsPage.unities.sportUnityModal.complement')}
                  isOptional={true}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'address.complement'}
              control={control}
            />
          </Grid2>
        </Grid2>
        <Grid2 size={12} display="flex" alignItems="center" justifyContent="flex-start">
          <Controller
            render={({ field: { value, onChange } }) => (
              <SwitchComponent
                label="Ocultar vídeos dessa unidade da listagem de vídeos"
                value={value || false}
                onChange={onChange}
              />
            )}
            name={'hideVideosFromListing'}
            control={control}
          />
        </Grid2>
        <ButtonComponent
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit(handleEdition)}
        >
          {t('sportsPage.unities.sportUnityModal.saveButton')}
        </ButtonComponent>
      </Grid2>
    </ModalComponent>
  );
};

export default SportUnityEditModal;
